// src/types/compliance.ts

// ─── ENUMS ──────────────────────────────────────────────────

export enum LocalAuthority {
  WAKEFIELD = 'WAKEFIELD',
  LEEDS = 'LEEDS',
  BRADFORD = 'BRADFORD',
  KIRKLEES = 'KIRKLEES',
}

export enum DependencyLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  COMPLEX = 'COMPLEX',
}

export enum IncidentStatus {
  OPEN = 'OPEN',
  INVESTIGATING = 'INVESTIGATING',
  AWAITING_NOTIFICATIONS = 'AWAITING_NOTIFICATIONS',
  CLOSED = 'CLOSED',
}

export enum ActionStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
}

export enum AuditStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
}

export enum TrainingStatus {
  CURRENT = 'CURRENT',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
}

export enum PolicyStatus {
  CURRENT = 'CURRENT',
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED',
  OVERDUE = 'OVERDUE',
}

export enum RiskSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum ComplaintStatus {
  RECEIVED = 'RECEIVED',
  INVESTIGATING = 'INVESTIGATING',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export type PlanStatus =
  | 'Active Remediation'
  | 'Chasing Triggered'
  | 'Overdue Breach'
  | 'Successfully Resolved & Closed';

// ─── INTERFACES ─────────────────────────────────────────────

export interface Resident {
  id: string;
  identityCode: string;
  localAuthority: LocalAuthority;
  dependencyLevel: DependencyLevel;
  dailyCareHoursRequired: number;
  createdAt: string;
  updatedAt: string;
}

export interface Incident {
  id: string;
  incidentRef: string;
  residentId: string;
  resident: Resident;
  status: IncidentStatus;
  incidentDate: string;
  description: string;
  immediateActionsTaken: string;
  rootCauseAnalysis: string | null;
  lessonsLearned: string | null;
  localAuthorityNotified: boolean;
  localAuthorityNotifyDate: string | null;
  localAuthorityRef: string | null;
  cqcNotified: boolean;
  cqcNotifyDate: string | null;
  cqcRef: string | null;
  familyInformed: boolean;
  familyInformTime: string | null;
  closedAt: string | null;
  closedByManagerSignature: string | null;
  actionPlanItems: ComplianceTask[];
}

export interface ComplianceTask {
  id: string;
  incidentId: string | null;
  incident: Incident | null;
  residentId: string | null;
  resident: Resident | null;
  title: string;
  correctiveActionPlan: string;
  assignedOwner: string;
  status: ActionStatus;
  dueDate: string;
  completedAt: string | null;
  auditEvidencePortfolioTarget: string;
}

export interface Audit {
  id: string;
  title: string;
  category: 'Medication' | 'IPC' | 'Pressure Care' | 'Fire Safety' | 'Nutrition' | 'Dignity' | 'Governance';
  scheduledDate: string;
  completedDate: string | null;
  findings: string;
  actionsRequired: string;
  status: AuditStatus;
  evidenceUrl: string | null;
}

export interface TrainingRecord {
  id: string;
  staffName: string;
  staffRole: string;
  courseName: string;
  completionDate: string;
  expiryDate: string;
  status: TrainingStatus;
  certificateUrl: string | null;
}

export interface Policy {
  id: string;
  title: string;
  category: 'Clinical' | 'HR' | 'IPC' | 'Safeguarding' | 'Governance' | 'Operations';
  version: string;
  lastReviewed: string;
  nextReview: string;
  status: PolicyStatus;
  documentUrl: string | null;
}

export interface Risk {
  id: string;
  description: string;
  category: 'Clinical' | 'Operational' | 'Financial' | 'Regulatory' | 'Reputation';
  severity: RiskSeverity;
  mitigationAction: string;
  owner: string;
  reviewDate: string;
  status: 'OPEN' | 'MITIGATED' | 'CLOSED';
}

export interface Meeting {
  id: string;
  title: string;
  date: string;
  attendees: string[];
  minutes: string;
  actionItems: string[];
  status: 'SCHEDULED' | 'HELD' | 'CANCELLED';
}

export interface EquipmentInspection {
  id: string;
  equipmentName: string;
  serialNumber: string;
  inspectionType: 'LOLER' | 'PUWER' | 'Fire Safety' | 'Legionella' | 'Electrical';
  lastInspection: string;
  nextInspection: string;
  status: 'PASS' | 'FAIL' | 'PENDING';
  notes: string;
}

export interface Complaint {
  id: string;
  residentName: string;
  complainant: string;
  dateReceived: string;
  description: string;
  investigationStatus: ComplaintStatus;
  outcome: string | null;
  actionsTaken: string | null;
  closedAt: string | null;
}

export interface DailyChecks {
  day_1_signed: boolean;
  day_1_notes: string;
  day_2_signed: boolean;
  day_2_notes: string;
  day_3_signed: boolean;
  day_3_notes: string;
}

export interface WeeklyChecks {
  week_1_passed: boolean;
  week_1_verified_by: string;
  week_1_notes: string;
  week_2_passed: boolean;
  week_2_verified_by: string;
  week_2_notes: string;
}

export interface MonthlyChecks {
  is_signed_off: boolean;
  signed_off_by_manager: string;
  sign_off_timestamp: string | null;
  final_evidence_document_url: string | null;
}

export interface CompetencyActionPlan {
  action_id: string;
  parent_session_id: string;
  failed_question_ids: string[];
  staff_id: string;
  staff_name: string;
  deficiency_details: string;
  corrective_action_required: string;
  assigned_mentor_name: string;
  daily_verification_checks: DailyChecks;
  weekly_manager_review: WeeklyChecks;
  monthly_governance_signoff: MonthlyChecks;
  plan_status: PlanStatus;
  created_at: string;
  updated_at: string;
  due_dates: { daily: string[]; weekly: string[]; monthly: string };
  actual_completed_at: string | null;
  actual_chased_at: string | null;
  chase_deadline: string;
  closure_deadline: string;
}